/*
 * CHANGE STATUS…: ONLY WHAT FITS NOW, EACH WITH ITS REASONS AND WHAT IT CHANGES.
 *
 * Opened from the position pill (the brief, item 11; frame 1i). It replaces the
 * old card's Quick actions (Mark churned…, Mark as vendor…, Not a lead…) and the
 * board's ⋯ menu logic, so the record gets every hand move the CRM has, in one
 * place, offered only where it makes sense:
 *
 *   Move to            the other open stages, and Closed won… with a won date
 *   Hold until…        a snooze (follow_up_state): the record keeps its stage
 *   Not now            only when something is asking (a line in Next)
 *   Remove from pipeline…   a park, with a reminder or none
 *   Closed lost…       only when it has been EARNED (src/changes/lostGate.js:
 *                      a competitor needs a call; declined and closed out need a
 *                      call AND pricing), each reason greyed with why otherwise
 *   Disqualify…        junk, bad fit: never again
 *   Mark as vendor     its confirm says what happens (recordModel.mjs vendorEffects)
 *   Not a lead…        never on a client or a former client
 *   Mark churned…      on a client only, with the notice date
 *
 * THE REASON LISTS ARE THE SERVER'S. STATUS_REASONS below must equal
 * src/db/crm.js STATUS_REASONS, key for key and in order, because the PATCH route
 * refuses any other reason; test/record-endings.test.js reads that file and fails
 * when they drift (the stage-order.test.js arrangement: the client build cannot
 * import from src/). Which of them a person PICKS is a separate, smaller list:
 * the sweeps' own parks ('ignored') and the old reasons kept only for history
 * ('misaligned') are labelled but not offered.
 *
 * Pure. The component renders `changeStatusOptions(...)`, asks `confirmFor(...)`
 * for the confirm's sentence and fields, and sends `writesFor(...)`.
 */
import { SNOOZE_LABELS } from "../hold.mjs";
import { vendorEffects } from "../recordModel.mjs";
import { shortDate, longDate, addDays, plural, fullName, firstName, listWords, toDate } from "./format.mjs";
import { pointOfContact, STAGE_LABELS, LOST_TO_PARK_DAYS, hasSignedCopy } from "./situation.mjs";

/** src/db/crm.js STATUS_REASONS, exactly (pinned by test/record-endings.test.js). */
export const STATUS_REASONS = {
  not_a_fit: ["junk", "bad_fit"],
  lost: ["chose_competitor", "declined", "closed_out"],
  recycled: ["stopped_chasing", "ghosted", "ignored", "no_budget_now", "timing", "browsing", "misaligned"],
};

/** Every reason in words: the frame's ("Declined after pricing"). */
export const REASON_LABELS = {
  junk: "Junk",
  bad_fit: "Bad fit",
  chose_competitor: "Chose a competitor",
  declined: "Declined after pricing",
  closed_out: "Closed out",
  stopped_chasing: "Stopped chasing",
  ghosted: "Ghosted",
  ignored: "Ignored",
  no_budget_now: "No budget now",
  timing: "Timing",
  browsing: "Browsing",
  misaligned: "Misaligned",
};

/*
 * The reasons a person picks. 'ignored' is rule 2b's park (the sweep writes it,
 * Gabriel never has), and 'misaligned' is kept for the parks that already carry
 * it; the frame offers neither. Every offered reason is one STATUS_REASONS holds.
 */
export const OFFERED_REASONS = {
  not_a_fit: ["junk", "bad_fit"],
  lost: ["chose_competitor", "declined", "closed_out"],
  recycled: ["stopped_chasing", "ghosted", "no_budget_now", "timing", "browsing"],
};
export const NOT_OFFERED = { recycled: { ignored: "the 2b sweep's park", misaligned: "kept for older parks" } };

/** "Remind me" for Remove from pipeline, in months, then a date or none. */
export const REMIND_MONTHS = [3, 6, 12];
export const DEFAULT_REMIND_MONTHS = 6;

/** Hold until…: the server's own presets (hold.mjs SNOOZE_LABELS), then a date. */
export const HOLD_PRESETS = Object.entries(SNOOZE_LABELS).map(([id, label]) => ({ id, label }));

// Manual moves go any direction (BoardPage moveCompany), and the words beside each
// stage say what the move means from where the company is now (frame 1i, drawn at
// Agreement + legal, which is why the backward words are the frame's).
const OPEN_STAGES = ["new", "call_scheduled", "in_progress", "proposal_sent", "evaluating", "agreement"];
const BACKWARD = {
  new: "start over",
  call_scheduled: "a new call is booked",
  in_progress: "back to talking",
  proposal_sent: "agreement pulled back",
  evaluating: "they’re reconsidering",
};
const FORWARD = {
  new: "start over",
  call_scheduled: "a call is booked",
  in_progress: "the call happened",
  proposal_sent: "pricing went out",
  evaluating: "they answered the pricing",
  agreement: "the agreement went out",
};

const ACTIVE_LINE = (a) => a && a.kind && a.kind !== "meeting_today" && a.kind !== "meeting_soon";

/** A former client: churned, or won at any point (markNotALead refuses both). */
export function wasClient(company) {
  return !!company && (company.status === "client" || company.status === "churned" || !!company.won_at);
}

/** Which lost reasons are earned, and why the others are not (lostGate.js). */
export function lostReasons(lostEvidence) {
  const ev = lostEvidence || {};
  const call = ev.had_call === true;
  const pricing = ev.got_pricing === true;
  return OFFERED_REASONS.lost.map((id) => {
    const needsPricing = id !== "chose_competitor";
    const ok = call && (!needsPricing || pricing);
    const why = ok ? null : !call ? "no call on record yet" : "no pricing on record yet";
    return { id, label: REASON_LABELS[id], enabled: ok, why };
  });
}

/**
 * THE MENU. Returns sections, each [{ id, label, hint, confirm, disabled?, why?,
 * presets? }]; a section with no items is left out. `confirm` names the confirm
 * the item opens (or null for a move that writes straight away).
 */
export function changeStatusOptions({ company = null, contact = null, act = null } = {}) {
  if (!company) {
    // No company: the record's only ending is the person's own.
    return contact && contact.kind !== "junk"
      ? [{ id: "end", label: null, items: [{ id: "not_a_lead", label: "Not a lead…", hint: "never a sales conversation", confirm: "not_a_lead" }] }]
      : [];
  }
  const status = company.status;
  const stage = company.pipeline_stage;
  const sections = [];
  const actions = ((act && act.actions) || []).filter(ACTIVE_LINE);

  if (status === "client") {
    sections.push({
      id: "end",
      label: null,
      items: [
        { id: "churn", label: "Mark churned…", hint: "notice date and reason", confirm: "churn" },
        { id: "vendor", label: "Mark as vendor", hint: "they sell to us", confirm: "vendor" },
      ],
    });
    return sections;
  }
  if (status === "churned") {
    // A churned account is read-only on the record: it comes back through a new
    // conversation, which starts at New.
    sections.push({ id: "move", label: "Move to", items: [{ id: "move:new", stage: "new", label: "New", hint: "a new sales conversation", confirm: null }] });
    return sections;
  }

  // Move to: every open stage but the current one, then Closed won….
  const here = OPEN_STAGES.indexOf(stage);
  const live = status === "prospect" && here >= 0;
  const moves = OPEN_STAGES.filter((s) => !(live && s === stage)).map((s) => ({
    id: `move:${s}`,
    stage: s,
    label: STAGE_LABELS[s],
    hint: live && OPEN_STAGES.indexOf(s) < here ? BACKWARD[s] : status === "prospect" ? FORWARD[s] : s === "new" ? "back on the board" : FORWARD[s],
    confirm: null,
  }));
  if (status === "prospect" || status === "recycled" || status === "lost") {
    moves.push({
      id: "won",
      label: "Closed won…",
      hint: hasSignedCopy(company, act) ? "pick the won date" : "pick the won date · no signed copy yet",
      confirm: "won",
    });
  }
  sections.push({ id: "move", label: "Move to", items: moves });

  // Hold and Not now: only for a prospect on the board, which is what a queue holds.
  if (status === "prospect" && stage) {
    const hold = [{ id: "hold", label: "Hold until…", hint: "›", confirm: "hold", presets: [...HOLD_PRESETS, { id: "date", label: "A date…" }] }];
    if (actions.length) hold.push({ id: "not_now", label: "Not now", hint: "until tomorrow", confirm: null });
    sections.push({ id: "hold", label: null, items: hold });
  }

  const end = [];
  if (status === "prospect" || status === "lost") {
    end.push({ id: "remove", label: "Remove from pipeline…", hint: "parks it, wakes on a reminder", confirm: "remove" });
  }
  if (status === "prospect") {
    const reasons = lostReasons(act && act.lost_evidence);
    const any = reasons.some((r) => r.enabled);
    end.push({
      id: "lost",
      label: "Closed lost…",
      hint: "competitor, declined after pricing, closed out",
      confirm: "lost",
      disabled: !any,
      why: any ? null : "Lost is earned: a call and their no. Remove from pipeline instead.",
    });
  }
  if (status !== "not_a_fit") end.push({ id: "disqualify", label: "Disqualify…", hint: "junk, bad fit", confirm: "disqualify" });
  if (status !== "vendor") end.push({ id: "vendor", label: "Mark as vendor", hint: "they sell to us", confirm: "vendor" });
  if (!wasClient(company)) end.push({ id: "not_a_lead", label: "Not a lead…", hint: "never a sales conversation", confirm: "not_a_lead" });
  if (end.length) sections.push({ id: "end", label: null, items: end });
  return sections;
}

/** Every item id the menu offers, flat: for tests and the phone's More sheet. */
export function offeredIds(sections) {
  return (sections || []).flatMap((s) => s.items.map((i) => i.id));
}

/* ------------------------------------------------------------ the confirms */

function monthsFrom(now, months) {
  const d = new Date(toDate(now).getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

function ymd(d) {
  const x = toDate(d);
  if (!x) return null;
  const p = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
}

/*
 * The people who stay on the record, in words, with the verb that fits them:
 * "Priya, Tom and Ella stay", "Priya stays". A person with two records is one
 * person here (Pluto's duplicate Shachar read "Shachar and Shachar").
 */
function peopleWords(company) {
  const names = [...new Set(((company && company.contacts) || []).filter((c) => c.kind !== "junk").map((c) => firstName(c)).filter(Boolean))];
  return names.length ? `${listWords(names)} ${names.length === 1 ? "stays" : "stay"}` : "";
}

/**
 * The confirm for one item: { title, fields, sentence(choice) , confirmLabel }.
 * `fields` names what the card asks for ('reason', 'remind', 'date', 'note',
 * 'notice'); `sentence(choice)` is what it will change, in plain words, worked
 * out from the choice so far.
 */
export function confirmFor(id, { company = null, contact = null, act = null, now = new Date() } = {}) {
  const name = (company && company.name) || "This company";
  const openLines = ((act && act.actions) || []).filter(ACTIVE_LINE).length;
  const closes = openLines ? ` Your ${plural(openLines, "open line")} in Next ${openLines === 1 ? "closes" : "close"}.` : "";
  const people = peopleWords(company);
  switch (id) {
    case "remove":
      return {
        title: `Remove ${name} from the pipeline?`,
        fields: ["reason", "remind", "note"],
        reasons: OFFERED_REASONS.recycled.map((r) => ({ id: r, label: REASON_LABELS[r] })),
        remind: [...REMIND_MONTHS.map((m) => ({ id: `${m}m`, months: m, label: m === 3 ? "In 3 months" : `${m} months` })), { id: "date", label: "A date…" }, { id: "none", label: "No reminder" }],
        defaults: { reason: "stopped_chasing", remind: `${DEFAULT_REMIND_MONTHS}m` },
        confirmLabel: "Confirm",
        sentence: (c = {}) => {
          const until = remindDate(c, now);
          const why = c.reason ? `, with “${String(REASON_LABELS[c.reason] || c.reason).toLowerCase()}” as the reason` : "";
          const park = until ? `parks until ${longDate(until)}` : "parks with no reminder";
          return `${name} leaves the board and ${park}${why}.${closes}${people ? ` ${people} on the record.` : ""} It comes back by itself if they write.`;
        },
      };
    case "lost": {
      const reasons = lostReasons(act && act.lost_evidence);
      return {
        title: `Close ${name} as lost?`,
        fields: ["reason", "note"],
        reasons,
        defaults: { reason: (reasons.find((r) => r.enabled) || {}).id || null },
        confirmLabel: "Confirm",
        sentence: (c = {}) => {
          const parks = addDays(now, LOST_TO_PARK_DAYS);
          const why = c.reason ? ` with “${String(REASON_LABELS[c.reason]).toLowerCase()}”` : "";
          return `${name} moves to Closed lost${why}, and parks itself on ${longDate(parks)}.${closes} The quote and the history stay.`;
        },
      };
    }
    case "disqualify":
      return {
        title: `Disqualify ${name}?`,
        fields: ["reason", "note"],
        reasons: OFFERED_REASONS.not_a_fit.map((r) => ({ id: r, label: REASON_LABELS[r] })),
        defaults: { reason: "bad_fit" },
        confirmLabel: "Confirm",
        sentence: (c = {}) => {
          const poc = pointOfContact(company);
          const junk = c.reason === "junk" && poc ? ` ${fullName(poc)} is marked junk too.` : "";
          return `${name} leaves the board and is never offered again.${closes}${junk} Its timeline is kept.`;
        },
      };
    case "vendor":
      return {
        title: `Mark ${name} as a vendor?`,
        fields: [],
        confirmLabel: "Confirm",
        sentence: () => vendorEffects({ company, poc: pointOfContact(company) }).join(" "),
      };
    case "not_a_lead": {
      const who = contact ? fullName(contact) : "This person";
      return {
        title: `${who} is not a lead?`,
        fields: ["note"],
        confirmLabel: "Not a lead",
        sentence: () => {
          const others = ((company && company.contacts) || []).filter((c) => contact && String(c.id) !== String(contact.id));
          const goes = company && company.status === "prospect" && others.length === 0;
          const email = contact && contact.email ? `, and ignores ${contact.email} from then on: no contact, no email and no proposal, however often they write` : "";
          const co = company ? (goes ? ` ${company.name} goes with them if it holds nothing else.` : ` ${company.name} stays, with its timeline.`) : "";
          return `Deletes this contact and their whole timeline${email}.${co} Kept as a receipt under Ignored on the Leads page.`;
        },
      };
    }
    case "churn":
      return {
        title: `Mark ${name} churned?`,
        fields: ["notice", "reason_text", "note"],
        defaults: { notice: ymd(now) },
        confirmLabel: "Confirm",
        sentence: (c = {}) => {
          const notice = toDate(c.notice ? `${c.notice}T12:00:00` : now);
          const ends = addDays(notice, 30);
          return `Service ends ${longDate(ends)}, 30 days after the notice, and that is the churn date. The notice date goes on the timeline.`;
        },
      };
    case "won":
      return {
        title: `Mark ${name} won?`,
        fields: ["date", "note"],
        defaults: { date: ymd(now) },
        confirmLabel: "Confirm",
        sentence: (c = {}) => {
          const day = c.date ? toDate(`${c.date}T12:00:00`) : now;
          const signed = hasSignedCopy(company, act) ? "" : " No signed copy is on record yet, and the header will say so.";
          return `${name} becomes a client, won on ${shortDate(day, { now })}.${signed} The welcome and the finance intro come up in Next.`;
        },
      };
    case "hold":
      return {
        title: `Hold ${name}?`,
        fields: ["preset", "date", "note"],
        presets: [...HOLD_PRESETS, { id: "date", label: "A date…" }],
        defaults: { preset: "2_weeks" },
        confirmLabel: "Confirm",
        sentence: (c = {}) => {
          const label = c.preset === "date" ? (c.date ? `until ${longDate(toDate(`${c.date}T12:00:00`))}` : "until the date you pick") : (SNOOZE_LABELS[c.preset] || "for now").toLowerCase();
          return `${name} keeps its stage and leaves your queues ${label.startsWith("until") || label.startsWith("after") ? label : `until ${label}`}.`;
        },
      };
    default:
      return null;
  }
}

function remindDate(choice, now) {
  if (!choice || choice.remind === "none") return null;
  if (choice.remind === "date") return choice.date ? toDate(`${choice.date}T12:00:00`) : null;
  const m = Number(String(choice.remind || `${DEFAULT_REMIND_MONTHS}m`).replace("m", ""));
  return monthsFrom(now, Number.isFinite(m) ? m : DEFAULT_REMIND_MONTHS);
}

/* ------------------------------------------------------------ the writes */

/**
 * The API calls an item makes, in order: [{ method, path, body }]. The note, when
 * given, goes on the timeline as a note activity (status_reason has no room for
 * prose). The writes are the existing endpoints (build map [22]); nothing here is
 * a new route.
 */
export function writesFor(id, choice = {}, { company = null, contact = null, now = new Date() } = {}) {
  const co = company || {};
  const note = String(choice.note || "").trim();
  const noteWrite = (title) =>
    note ? [{ method: "POST", path: "/activities", body: { type: "note", company_id: co.id || null, contact_id: contact ? contact.id : null, title, body: note } }] : [];
  const poc = pointOfContact(company);

  if (String(id).startsWith("move:")) {
    const stage = String(id).slice(5);
    return [{ method: "PATCH", path: `/companies/${co.id}`, body: { status: "prospect", pipeline_stage: stage } }];
  }
  switch (id) {
    case "won": {
      const day = choice.date ? `${choice.date}T12:00:00` : toDate(now).toISOString();
      return [{ method: "PATCH", path: `/companies/${co.id}`, body: { status: "client", won_at: toDate(day).toISOString() } }, ...noteWrite("Closed won")];
    }
    case "hold": {
      const body = choice.preset === "date" && choice.date ? { until: choice.date, note: note || null } : { preset: choice.preset || "2_weeks", note: note || null };
      return [{ method: "POST", path: `/follow-ups/${co.id}/snooze`, body }];
    }
    case "not_now":
      return [{ method: "POST", path: `/follow-ups/${co.id}/skip`, body: {} }];
    case "remove": {
      const until = remindDate(choice, now);
      return [
        { method: "PATCH", path: `/companies/${co.id}`, body: { status: "recycled", status_reason: choice.reason || "stopped_chasing", recycle_until: until ? ymd(until) : null } },
        ...noteWrite(`Removed from the pipeline · ${String(REASON_LABELS[choice.reason || "stopped_chasing"]).toLowerCase()}`),
      ];
    }
    case "lost":
      return [
        { method: "PATCH", path: `/companies/${co.id}`, body: { status: "lost", status_reason: choice.reason } },
        ...noteWrite(`Closed lost · ${String(REASON_LABELS[choice.reason] || choice.reason).toLowerCase()}`),
      ];
    case "disqualify": {
      const out = [{ method: "PATCH", path: `/companies/${co.id}`, body: { status: "not_a_fit", status_reason: choice.reason } }];
      out.push(...noteWrite(`Disqualified · ${String(REASON_LABELS[choice.reason] || choice.reason).toLowerCase()}`));
      if (choice.reason === "junk" && co.primary_contact_id) out.push({ method: "POST", path: `/contacts/${co.primary_contact_id}/junk`, body: {} });
      return out;
    }
    case "vendor":
      return co.primary_contact_id
        ? [{ method: "POST", path: `/contacts/${co.primary_contact_id}/vendor`, body: {} }]
        : [{ method: "PATCH", path: `/companies/${co.id}`, body: { status: "vendor" } }];
    case "not_a_lead":
      return [{ method: "POST", path: `/contacts/${contact ? contact.id : poc && poc.id}/not-a-lead`, body: { note: note || null } }];
    case "churn": {
      const notice = toDate(choice.notice ? `${choice.notice}T12:00:00` : now);
      const ends = addDays(notice, 30);
      const reason = String(choice.reason_text || "").trim();
      return [
        { method: "PATCH", path: `/companies/${co.id}`, body: { status: "churned", churned_at: ends.toISOString(), status_reason: reason || null } },
        {
          method: "POST",
          path: "/activities",
          body: {
            type: "note",
            company_id: co.id,
            title: "Churn notice",
            body: `Notice received ${longDate(notice)}. Service ends ${longDate(ends)} (notice + 30 days).${reason ? ` Reason: ${reason}` : ""}${note ? ` ${note}` : ""}`,
          },
        },
      ];
    }
    default:
      return [];
  }
}
